import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { EditorView } from 'prosemirror-view';
import type { RenderedDomContext } from '@eigenpal/docx-editor-react/plugin-api';
import type { DocxEditorRef } from '@eigenpal/docx-editor-react';
import { extractTrackedChanges } from '@eigenpal/docx-editor-react/hooks';
import { acceptAllChanges } from '@eigenpal/docx-editor-core/prosemirror/commands';
import { loadDocxEditor, type DocxEditorComponent } from './docxEditor';
import { resolveFixes, type DocumentFix } from './documentFixes';
import { DocxFixOverlay } from './DocxFixOverlay';
import { DocxFixMarginCard } from './DocxFixMarginCard';

/** Imperative surface so the reference column (outside the editor) can drive fixes in the document. */
export interface DocxFixHandle {
  /** Scroll to / activate a fix's highlight. */
  jumpTo: (id: string) => void;
  /** Apply a fix's replacement (a real edit) and report it applied. */
  applyFix: (id: string) => void;
  /** Reopen a closed floating card (and activate it) — used by the reference-column card. */
  reopen: (id: string) => void;
  /**
   * Accept every tracked change (reeve's fixes + the user's edits) into the text and return the saved
   * .docx buffer, so the owner can write it back to storage. Null if the editor isn't ready.
   */
  acceptAndSave: () => Promise<ArrayBuffer | null>;
  /** Discard all in-editor edits by reloading from a (already-normalized) buffer. */
  reload: (buffer: ArrayBuffer) => void;
}

/** The editor's natural root width (816px US-Letter page + ~40px gutter) — used for the stacked width fit. */
const EDITOR_WIDTH = 856;
/** The bare US-Letter page width, and the gutter the editor keeps around it — used to size the wide column. */
const PAGE_WIDTH = 816;
const EDITOR_GUTTER = 50;
/** Floating fix-card geometry: width, gap to the right of the snippet, and how far the title sits above the
 *  fix text so that fix text lines up with the snippet's top line. */
const CARD_WIDTH = 252;
const CARD_GAP = 24;
const CARD_TITLE_OFFSET = 34;
/** One page's natural laid-out height: the 1056px US-Letter page + the paged-editor's ~96px of padding. */
const ONE_PAGE_HEIGHT = 1152;
/** Horizontal room to leave around the page so it isn't flush to the pane edges. */
const FIT_MARGIN = 48;
/** Vertical breathing room so a fitted page isn't edge-to-edge top/bottom. */
const FIT_MARGIN_Y = 20;
const MIN_ZOOM = 0.2;
/** Don't shrink the canvas below this even on a very short window. */
const MIN_PANE_H = 320;
/** Side-by-side layout breakpoint — mirrors the `@media (min-width: 1401px)` shell in app-documents.css. */
const WIDE_QUERY = '(min-width: 1401px)';
/** Keep the reference column at least this wide when sizing the editor column to hug the page. */
const REF_MIN_WIDTH = 400;
/** The `.docs-split` grid column gap — kept in sync so the editor-column width math leaves the reference room. */
const SPLIT_GAP = 20;

interface Fit {
  zoom: number;
  /** Explicit canvas height (px) in the side-by-side layout; null = let it flow (stacked/narrow). */
  paneH: number | null;
  /** Editor COLUMN width (px) on wide screens, so it hugs the page; null = stacked (column not sized). */
  editorW: number | null;
}

const clampZoom = (z: number) => Math.max(MIN_ZOOM, Math.min(1, Math.round(z * 1000) / 1000));

/** Skip a card-position state update when nothing moved (avoids a measure→setState→measure loop). */
function shallowEqualPos(
  a: Record<string, { top: number; left: number }>,
  b: Record<string, { top: number; left: number }>,
): boolean {
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  return ak.every((k) => b[k] && a[k].top === b[k].top && a[k].left === b[k].left);
}

/**
 * Fit one page to the pane. On a wide (side-by-side) screen we bound the canvas to the space left down to the
 * viewport bottom and scale a single page to fit BOTH width and height — so a one-page document shows whole,
 * with no scrollbar and no empty space. On a narrow (stacked) screen we only fit width and let the page flow.
 *
 * The canvas height is the editor's OWN available space: viewport bottom minus the canvas top and the fixed
 * padding below the split. Crucially it's measured against the SPLIT bottom, not the page bottom, so a taller
 * info column beside the editor never drags the editor's size around — the editor fills its own column, and an
 * over-long info column is the only thing that can add a (real, content-revealing) scroll.
 */
function measureFit(el: HTMLElement): Fit {
  if (!window.matchMedia(WIDE_QUERY).matches) {
    // STACKED: one full-width column — fit the page to the pane width and let it flow.
    const wrapW = el.clientWidth;
    const fitW = wrapW ? (wrapW - FIT_MARGIN) / EDITOR_WIDTH : 1;
    return { zoom: clampZoom(Math.min(fitW, 1)), paneH: null, editorW: null };
  }

  const rect = el.getBoundingClientRect();
  const apage = el.closest('.apage');
  const split = el.closest('.docs-split');
  // Fixed padding after the split (page padding-bottom) — measured off the SPLIT, so it excludes any overhang
  // from a taller info column and stays independent of the editor's own height.
  const padBelow =
    apage && split
      ? Math.max(0, apage.getBoundingClientRect().bottom - split.getBoundingClientRect().bottom)
      : 0;
  const availH = Math.max(MIN_PANE_H, Math.round(window.innerHeight - rect.top - padBelow));
  const fitH = (availH - FIT_MARGIN_Y) / ONE_PAGE_HEIGHT;

  // Width the editor may take while leaving the reference column at least REF_MIN_WIDTH. Measured off the
  // SPLIT's total width — which does NOT depend on the column we're about to size — so this can't feed back.
  const splitW = split ? split.clientWidth : 0;
  const fitW = splitW ? (splitW - REF_MIN_WIDTH - SPLIT_GAP - EDITOR_GUTTER) / PAGE_WIDTH : 1;

  const zoom = clampZoom(Math.min(fitW, fitH, 1));
  // Size the editor COLUMN to hug the fitted page (page width at this zoom + gutter), so the reference column
  // gets the rest instead of the document floating in a too-wide pane. Fix cards float OVER the page.
  return { zoom, paneH: availH, editorW: Math.round(PAGE_WIDTH * zoom + EDITOR_GUTTER) };
}

/**
 * The editor zooms by `transform: scale()`, which shrinks the page VISUALLY but leaves its LAYOUT box at full
 * size — so the workspace reserves the unscaled ~864px width (and unscaled height), floating the page in a
 * too-wide, too-tall pane. Collapse that reservation with negative margins derived from the element's own
 * transform (scale + origin), WITHOUT touching the transform itself — the editor's cursor hit-testing depends
 * on that transform, and this leaves it intact (verified).
 */
function compensateZoomLayout(wrapEl: HTMLElement): void {
  const scroller = wrapEl.querySelector('.docx-editor__scroll-container');
  if (!scroller) return;
  let scaled: HTMLElement | null = null;
  for (const el of scroller.querySelectorAll<HTMLElement>('*')) {
    const t = getComputedStyle(el).transform;
    if (t.startsWith('matrix') && t !== 'matrix(1, 0, 0, 1, 0, 0)') {
      scaled = el;
      break;
    }
  }
  if (!scaled) return;
  const cs = getComputedStyle(scaled);
  const z = parseFloat(cs.transform.slice(cs.transform.indexOf('(') + 1));
  if (!(z > 0 && z < 0.999)) {
    scaled.style.margin = '';
    return;
  }
  const [ox, oy] = cs.transformOrigin.split(' ').map((v) => parseFloat(v));
  const wu = scaled.offsetWidth; // unscaled layout size (margins don't affect offsetWidth, so this is stable)
  const hu = scaled.offsetHeight;
  scaled.style.marginLeft = `${-Math.round(ox * (1 - z))}px`;
  scaled.style.marginRight = `${-Math.round((wu - ox) * (1 - z))}px`;
  scaled.style.marginTop = `${-Math.round(oy * (1 - z))}px`;
  scaled.style.marginBottom = `${-Math.round((hu - oy) * (1 - z))}px`;
}

/**
 * The document editor surface, wired to docx-editor.dev (`@eigenpal/docx-editor-react`).
 *
 * SAFE to ship before a converted .docx exists: the (heavy) editor bundle loads on demand — and only when
 * there's a `documentBuffer` — otherwise this renders `fallback` (the file viewer). Client-only (the editor
 * needs the DOM at mount), loaded through the shared memoized loader so the idle prefetch and this mount share
 * one download.
 *
 * FIT: the page is a fixed 816×1056 US-Letter box. We wrap it and scale it with the CSS `zoom` property (which,
 * unlike a transform, scales LAYOUT too — no phantom scroll). On a wide screen we bound the canvas to the
 * viewport and fit a single page to it, centred (CSS grid + `margin:auto`) — a one-page doc shows whole with no
 * scrollbar; a longer doc scrolls INSIDE the canvas while the page header stays put.
 */
export const DocxEditorPane = forwardRef<
  DocxFixHandle,
  {
    documentBuffer: ArrayBuffer | null;
    onSave?: (buffer: ArrayBuffer) => void;
    fallback: ReactNode;
    /** Compliance findings to overlay (highlights + cards). Never written to the .docx. */
    fixes?: DocumentFix[];
    /** The finding whose card is open (controlled). */
    activeFixId?: string | null;
    onActiveFixChange?: (id: string | null) => void;
    /** Reports which fixes resolved to a location (so the reference column can show a Jump button). */
    onLocatedChange?: (locatedIds: string[]) => void;
    /** Fires after a fix is applied to the document (so the owner can drop it from the list). */
    onFixApplied?: (id: string) => void;
    /**
     * Fires when the document gains or loses pending changes — i.e. whether there is anything the user
     * could Accept or Revert. reeve gates the Accept/Revert buttons and save-back on this.
     */
    onDirtyChange?: (dirty: boolean) => void;
  }
>(function DocxEditorPane(
  {
    documentBuffer,
    onSave,
    fallback,
    fixes = [],
    activeFixId = null,
    onActiveFixChange,
    onLocatedChange,
    onFixApplied,
    onDirtyChange,
  },
  ref,
) {
  const [Editor, setEditor] = useState<DocxEditorComponent | null>(null);
  const [fit, setFit] = useState<Fit>({ zoom: 1, paneH: null, editorW: null });
  const editorRef = useRef<DocxEditorRef | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const latestZoom = useRef(1);
  const [view, setView] = useState<EditorView | null>(null);
  // Latest view for callbacks that must read live state (onChange, applyFix, acceptAndSave) without
  // re-binding to a stale render.
  const viewRef = useRef<EditorView | null>(null);
  const [renderedCtx, setRenderedCtx] = useState<RenderedDomContext | null>(null);
  // Bumped after the doc changes (apply) and after the zoom settles, to re-resolve ranges / re-measure rects.
  const [docVersion, setDocVersion] = useState(0);
  // Cards the user closed (×). The highlight stays; clicking the snippet or the reference card reopens it.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!documentBuffer || Editor) return;
    let alive = true;
    void loadDocxEditor().then((Cmp) => {
      if (alive && Cmp) setEditor(() => Cmp);
    });
    return () => {
      alive = false;
    };
  }, [documentBuffer, Editor]);

  const rafRef = useRef(0);
  const applyFit = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    // Measure in the next frame so layout has settled (the filename bar above the canvas can change height
    // after fonts/content land, which shifts the canvas top). Coalesce bursts of calls into one measurement.
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const node = wrapRef.current;
      if (!node) return;
      const next = measureFit(node);
      // Collapse the editor's unscaled-transform layout reservation so it hugs the page (and stays idempotent).
      compensateZoomLayout(node);
      // Size the editor COLUMN by writing a CSS var on the split (the grid owner). Clearing it (stacked/
      // fallback) lets the grid fall back to its default editor track.
      const split = node.closest('.docs-split');
      if (split instanceof HTMLElement) {
        if (next.editorW != null) split.style.setProperty('--doc-editor-w', `${next.editorW}px`);
        else split.style.removeProperty('--doc-editor-w');
      }
      setFit((prev) =>
        // Skip the state update when nothing changed, so refits settle instead of looping.
        prev.zoom === next.zoom && prev.paneH === next.paneH && prev.editorW === next.editorW
          ? prev
          : next,
      );
    });
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!documentBuffer || !Editor || !el) return;
    applyFit();
    // Observe both the canvas (its own size + our height corrections drive the convergence) and its PARENT (a
    // late change to the filename bar shifts the canvas without resizing it). The self-correcting measure
    // settles once the page bottom meets the viewport bottom, so these repeated fires can't loop.
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => applyFit()) : null;
    observer?.observe(el);
    if (el.parentElement) observer?.observe(el.parentElement);
    // Canvas height tracks the WINDOW height (a width-only observer may miss it) and the wide/stacked
    // breakpoint flips on width — so refit on every window resize too.
    window.addEventListener('resize', applyFit);
    return () => {
      cancelAnimationFrame(rafRef.current);
      observer?.disconnect();
      window.removeEventListener('resize', applyFit);
      // Release the editor-column width so a later fallback (PDF/empty) isn't pinned to a stale docx width.
      const split = el.closest('.docs-split');
      if (split instanceof HTMLElement) split.style.removeProperty('--doc-editor-w');
    };
  }, [documentBuffer, Editor, applyFit]);

  // Drive the editor's OWN zoom (not a CSS transform on our wrapper): the editor bakes the zoom into its
  // layout AND its click→caret math, so scaling it from outside would misplace the cursor. Feeding it setZoom
  // keeps rendering and hit-testing in the same coordinate space.
  useEffect(() => {
    latestZoom.current = fit.zoom;
    editorRef.current?.setZoom(fit.zoom);
    // The editor applies its scale transform asynchronously (a frame or two after setZoom). Re-collapse the
    // reservation across several frames so we catch it whenever it lands — compensate reads the live transform
    // each time and is idempotent once the zoom settles.
    let raf = 0;
    let tries = 0;
    const tick = () => {
      if (wrapRef.current) compensateZoomLayout(wrapRef.current);
      if (++tries < 6) raf = requestAnimationFrame(tick);
      // Pages have settled at the new zoom — nudge the fix overlay to re-measure its highlight rects.
      else setDocVersion((v) => v + 1);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fit.zoom]);

  // Apply the current zoom the moment the editor view is ready (a refit may compute the same value and not
  // re-fire the effect above), then measure to fit.
  const handleReady = useCallback(
    (v: EditorView) => {
      viewRef.current = v;
      setView(v);
      editorRef.current?.setZoom(latestZoom.current);
      applyFit();
    },
    [applyFit],
  );

  // ── Pending-change tracking + save/revert ──────────────────────────────────────────────────────────────
  // "Dirty" = the document has tracked changes (reeve's applied fixes and/or the user's own edits, since we
  // open in suggesting mode on an already-accepted baseline). Recomputed on every editor change.
  const dirtyRef = useRef(false);
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  });
  const recomputeDirty = useCallback(() => {
    const v = viewRef.current;
    const dirty = v ? extractTrackedChanges(v.state).entries.length > 0 : false;
    if (dirty !== dirtyRef.current) {
      dirtyRef.current = dirty;
      onDirtyChangeRef.current?.(dirty);
    }
  }, []);

  const acceptAndSave = useCallback(async (): Promise<ArrayBuffer | null> => {
    const v = viewRef.current;
    const ed = editorRef.current;
    if (!v || !ed) return null;
    // Finalize every tracked change into the text, then serialize the clean document to a buffer.
    acceptAllChanges()(v.state, v.dispatch);
    const buffer = await ed.save();
    dirtyRef.current = false;
    onDirtyChangeRef.current?.(false);
    return buffer ?? null;
  }, []);

  const reload = useCallback((buffer: ArrayBuffer) => {
    const ed = editorRef.current;
    if (!ed) return;
    void ed.loadDocumentBuffer(buffer).then(() => {
      dirtyRef.current = false;
      onDirtyChangeRef.current?.(false);
      setDocVersion((n) => n + 1);
    });
  }, []);

  // ── Compliance fix overlays ────────────────────────────────────────────────────────────────────────────
  // Resolve each finding's anchor to a live ProseMirror range against the current document.
  const resolved = useMemo(
    () => (view ? resolveFixes(view.state.doc, fixes) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- docVersion forces re-resolve after edits/zoom
    [view, fixes, docVersion],
  );

  // Report which fixes resolved to a location — WITHOUT depending on the callback's identity (call sites pass
  // an inline arrow) and ONLY when the located set actually changes. A naive [resolved, onLocatedChange] dep
  // with a parent that builds a fresh value each call (e.g. `new Set(ids)`) ping-pongs parent↔child on every
  // render → "Maximum update depth exceeded". The ref holds the latest callback; the key gates real changes.
  const onLocatedChangeRef = useRef(onLocatedChange);
  useEffect(() => {
    onLocatedChangeRef.current = onLocatedChange;
  });
  const locatedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const ids = resolved.filter((r) => r.range).map((r) => r.fix.id);
    const key = ids.join('|');
    if (key === locatedKeyRef.current) return;
    locatedKeyRef.current = key;
    onLocatedChangeRef.current?.(ids);
  }, [resolved]);

  const applyFix = useCallback(
    (id: string) => {
      const ed = editorRef.current;
      const v = viewRef.current;
      if (!ed || !v) return;
      const target = resolveFixes(v.state.doc, fixes).find((r) => r.fix.id === id);
      const fx = target?.fix;
      const op = fx?.fix;
      if (!target?.range || !op) return;
      // reeve's fix becomes a TRACKED change (suggesting mode) the user can Accept/Revert alongside their own.
      let ok = false;
      if (op.kind === 'insert') {
        // Pure, position-accurate insertion at the anchor edge (before → start, after → end). A raw dispatch in
        // suggesting mode is auto-wrapped as a tracked <w:ins> (verified), so this adds text — even a single
        // word mid-sentence — without touching surrounding text, and so can never conflict.
        v.dispatch(
          v.state.tr.insertText(op.text, op.where === 'before' ? target.range.from : target.range.to),
        );
        ok = true;
      } else {
        // replace/remove need a tracked DELETION (a raw tr.delete removes text outright rather than striking
        // it), which proposeChange does correctly. It anchors by the paragraph's Word paraId, found by text.
        const quote = fx?.anchor?.quote;
        if (!quote) return;
        const hits = ed.findInDocument(quote) ?? [];
        const hit = hits[(fx.anchor?.occurrence ?? 1) - 1] ?? hits[0];
        if (!hit) return;
        ok = ed.proposeChange({
          paraId: hit.paraId,
          search: quote,
          replaceWith: op.kind === 'replace' ? op.text : '',
          author: 'reeve',
        });
      }
      if (!ok) return;
      setDocVersion((n) => n + 1);
      recomputeDirty();
      onActiveFixChange?.(null);
      onFixApplied?.(id);
    },
    [fixes, onActiveFixChange, onFixApplied, recomputeDirty],
  );

  const jumpTo = useCallback(
    (id: string) => {
      onActiveFixChange?.(id);
      const r = resolved.find((x) => x.fix.id === id);
      if (r?.range && renderedCtx) {
        const coords = renderedCtx.getCoordinatesForPosition(r.range.from);
        const scroller = wrapRef.current;
        if (coords && scroller && scroller.scrollHeight > scroller.clientHeight + 4) {
          scroller.scrollTo({ top: Math.max(0, coords.y - 80), behavior: 'smooth' });
        }
      }
    },
    [resolved, renderedCtx, onActiveFixChange],
  );

  const closeFix = useCallback(
    (id: string) => {
      setDismissed((s) => {
        const n = new Set(s);
        n.add(id);
        return n;
      });
      onActiveFixChange?.(null);
    },
    [onActiveFixChange],
  );

  const reopenFix = useCallback(
    (id: string) => {
      setDismissed((s) => {
        if (!s.has(id)) return s;
        const n = new Set(s);
        n.delete(id);
        return n;
      });
      onActiveFixChange?.(id);
    },
    [onActiveFixChange],
  );

  useImperativeHandle(
    ref,
    () => ({ jumpTo, applyFix, reopen: reopenFix, acceptAndSave, reload }),
    [jumpTo, applyFix, reopenFix, acceptAndSave, reload],
  );

  // Floating cards hover OVER the page next to their snippet. We position them at NATURAL scale (outside the
  // editor's zoom transform) by reading each highlight element's live screen box and placing the card just
  // below it, clamped inside the canvas. Overlap is fine — the active card is z-lifted to the front.
  const cardsLayerRef = useRef<HTMLDivElement>(null);
  const [cardAt, setCardAt] = useState<Record<string, { top: number; left: number }>>({});
  const measureCards = useCallback(() => {
    const layer = cardsLayerRef.current;
    if (!layer) return;
    const lb = layer.getBoundingClientRect();
    const next: Record<string, { top: number; left: number }> = {};
    for (const { fix, range } of resolved) {
      if (!range) continue;
      const hl = document.querySelector(`[data-fix-id="${fix.id}"] div`);
      if (!(hl instanceof HTMLElement)) continue;
      const r = hl.getBoundingClientRect();
      // Sit the card to the RIGHT of the snippet with a gap, and lift it so the card's fix text (below the
      // title) lines up with the snippet's top line — clearer, and it covers far less of the document.
      const left = Math.max(6, Math.min(r.right - lb.left + CARD_GAP, lb.width - CARD_WIDTH - 6));
      const top = Math.max(4, r.top - lb.top - CARD_TITLE_OFFSET);
      next[fix.id] = { top, left };
    }
    setCardAt((prev) => (shallowEqualPos(prev, next) ? prev : next));
  }, [resolved]);

  // Re-place after the highlights render / the page re-lays-out (zoom, apply) and on scroll.
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(measureCards);
    const scroller = wrapRef.current;
    scroller?.addEventListener('scroll', measureCards, { passive: true });
    window.addEventListener('resize', measureCards);
    return () => {
      cancelAnimationFrame(raf);
      scroller?.removeEventListener('scroll', measureCards);
      window.removeEventListener('resize', measureCards);
    };
  }, [measureCards, docVersion, fit.zoom, fit.paneH, activeFixId, Editor]);

  if (!documentBuffer || !Editor) {
    return <>{fallback}</>;
  }

  return (
    <div
      className="docs-viewer__sheet docs-viewer__sheet--file docs-editor"
      ref={wrapRef}
      style={fit.paneH != null ? { height: fit.paneH } : undefined}
    >
      <div className="docs-editor__zoom">
        <Editor
          ref={editorRef}
          documentBuffer={documentBuffer}
          onSave={onSave}
          // The editor scales ITSELF to this zoom (see the setZoom effect) so its cursor hit-testing matches
          // what's on screen; initialZoom just reduces the first-frame flash before setZoom runs.
          initialZoom={fit.zoom}
          // Embed it clean: no toolbar/menu bar and no floating outline (index) toggle — reeve frames the
          // document, and edits/suggestions come from the assistant rather than a Word-style ribbon.
          showToolbar={false}
          showOutlineButton={false}
          // reeve edits are non-destructive suggestions: open in suggesting mode so the user's edits AND
          // reeve's applied fixes record as tracked changes, gated behind the Accept/Revert buttons. The
          // editor's own review sidebar is hidden (reeve surfaces issues through its compliance overlay) —
          // which also reclaims the right-margin space it would otherwise reserve.
          mode="suggesting"
          commentsSidebarOpen={false}
          onChange={recomputeDirty}
          onEditorViewReady={handleReady}
          onRenderedDomContextReady={setRenderedCtx}
          // Non-persistent compliance overlay (highlights + floating cards). Rendered in the editor's overlay
          // layer, so it never becomes part of the saved .docx.
          pluginOverlays={
            renderedCtx ? (
              <DocxFixOverlay
                resolved={resolved}
                ctx={renderedCtx}
                activeId={activeFixId}
                onActivate={(id) => onActiveFixChange?.(id)}
                onReopen={reopenFix}
              />
            ) : null
          }
        />
      </div>
      {/* Floating fix cards, at natural scale, hovering over the page next to each snippet. */}
      <div className="docs-fixcards" ref={cardsLayerRef}>
        {resolved.map(({ fix, range }) => {
          const at = range && cardAt[fix.id];
          if (!at || dismissed.has(fix.id)) return null;
          return (
            <div
              key={fix.id}
              style={{ position: 'absolute', top: at.top, left: at.left, zIndex: activeFixId === fix.id ? 100 : 10 }}
            >
              <DocxFixMarginCard
                fix={fix}
                active={activeFixId === fix.id}
                onActivate={(id) => onActiveFixChange?.(id)}
                onApply={applyFix}
                onClose={closeFix}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
});
